import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  createElementPdf,
  printWithBodyClass,
} from '../common/printableDocument'
import PdfActions from '../common/PdfActions'
import { getOrderById } from '../services/order_service'
import { calculateInvoiceSummary } from './invoiceCalculations'
import './Invoice.css'

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)

const formatNumber = (value) =>
  new Intl.NumberFormat('en-GH', { maximumFractionDigits: 2 }).format(
    Number(value) || 0,
  )

const formatPercentage = (value) => {
  const numericValue = Number(value) || 0
  const percentage =
    numericValue > 0 && numericValue < 1 ? numericValue * 100 : numericValue

  return new Intl.NumberFormat('en-GH', { maximumFractionDigits: 0 }).format(
    percentage,
  )
}

const formatDisplayDate = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
}

const getValue = (record, keys, fallback = '') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback

const getOrderBooks = (order) =>
  getValue(order, ['orderBooks', 'order_books', 'books', 'items'], [])

const printInvoice = () => printWithBodyClass('print-invoice')

const toInvoice = (order, invoiceId) => {
  const items = Array.isArray(getOrderBooks(order)) ? getOrderBooks(order) : []
  const orderType = getValue(order, ['order_type', 'orderType'], '')

  return {
    customer: {
      name: getValue(order, [
        'cust_name',
        'customer_name',
        'customerName',
        'name',
      ]),
      address: getValue(order, ['address', 'cust_address', 'location']),
      phone: getValue(order, ['phone', 'contact', 'cust_contact']),
    },
    invoiceNumber: getValue(
      order,
      ['orderid', 'orderId', 'order_id', 'id'],
      invoiceId,
    ),
    salesExecutive: getValue(order, [
      'sales_exec_name',
      'salesExecutive',
      'sales_executive',
      'sales_exec',
    ]),
    requestingOfficer: getValue(order, [
      'requesting_user',
      'requested_by',
      'user_fullname',
      'fullname',
    ]),
    printDate: new Date().toISOString(),
    orderType,
    order_type: orderType,
    items: items.map((item) => ({
      quantity: getValue(item, ['quantity', 'ordered_quantity', 'qty'], 0),
      cartons: item.cartons,
      packs: item.packs,
      copies: item.copies,
      description: getValue(item, [
        'book_title',
        'bookTitle',
        'description',
        'title',
        'name',
      ]),
      unitCost: getValue(
        item,
        ['unitprice', 'unitPrice', 'unitcost', 'unitCost'],
        0,
      ),
      discount: getValue(item, ['discount', 'discount_percent'], 0),
      subtotal: getValue(item, ['subtotal', 'total', 'line_total'], 0),
      totalReturned: getValue(
        item,
        ['totalreturned', 'totalReturned', 'returned_quantity'],
        0,
      ),
    })),
    summary: calculateInvoiceSummary(order, items),
  }
}

function Invoice() {
  const { invoiceId } = useParams()
  const [searchParams] = useSearchParams()
  const [invoice, setInvoice] = useState(null)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [downloadError, setDownloadError] = useState('')
  const [pdfBlob, setPdfBlob] = useState(null)
  const invoiceSheetRef = useRef(null)
  const downloadStartedRef = useRef(false)
  const isPdfDownload = searchParams.get('download') === 'pdf'

  useEffect(() => {
    let isMounted = true

    const loadInvoice = async () => {
      setIsLoading(true)
      setError('')
      try {
        const order = await getOrderById(invoiceId)
        if (!isMounted) return
        setInvoice(toInvoice(order, invoiceId))
      } catch (requestError) {
        if (isMounted) {
          setError(requestError.message || 'The invoice could not be loaded.')
        }
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    if (invoiceId) loadInvoice()
    else setIsLoading(false)

    return () => {
      isMounted = false
    }
  }, [invoiceId])

  useEffect(() => {
    if (!invoice || !isPdfDownload || downloadStartedRef.current) return

    downloadStartedRef.current = true
    createElementPdf(invoiceSheetRef.current)
      .then(setPdfBlob)
      .catch((downloadRequestError) => {
        downloadStartedRef.current = false
        setDownloadError(
          downloadRequestError.message || 'The PDF could not be downloaded.',
        )
      })
  }, [invoice, invoiceId, isPdfDownload])

  if (isLoading) {
    return (
      <div className='invoice-page invoice-page--empty'>Loading invoice...</div>
    )
  }

  if (!invoice) {
    return (
      <div className='invoice-page invoice-page--empty'>
        <p>{error || 'Invoice not found.'}</p>
      </div>
    )
  }

  const {
    customer,
    invoiceNumber,
    salesExecutive,
    requestingOfficer,
    printDate,
    items = [],
    summary,
    orderType,
    order_type,
  } = invoice

  const orderTypeVal = String(orderType || order_type || '').toLowerCase()

  const isExerciseBook =
    orderTypeVal.includes('exercise') ||
    (Array.isArray(items) &&
      items.some(
        (item) =>
          item.cartons !== undefined ||
          item.packs !== undefined ||
          item.copies !== undefined,
      ))

  return (
    <div className='invoice-page'>
      <div className='invoice-sheet' ref={invoiceSheetRef}>
        <header className='invoice-header'>
          <div className='invoice-header__brand'>
            <img className='invoice-header__logo' src='/logo1.jpg' alt='' />
            <h1>Buck Press Limited</h1>
          </div>
          <div className='invoice-header__date'>
            Date: <strong>{formatDisplayDate(printDate)}</strong>
          </div>
        </header>

        <section className='invoice-parties'>
          <div>
            <span className='invoice-parties__label'>From</span>
            <strong>Buck Press Limited</strong>
            <p>Accra - Nsawam Highway, Ofankor</p>
            <p>Tel: (233) 302-408061</p>
            <p>Email: publishing@buckpress.com</p>
          </div>
          <div>
            <span className='invoice-parties__label'>To</span>
            <strong>{customer.name}</strong>
            <p>{customer.address}</p>
            <p>Phone: {customer.phone}</p>
          </div>
          <div className='invoice-parties__meta'>
            <p>
              Invoice #<strong>{invoiceNumber}</strong>
            </p>
            <p>
              Sales Executive: <strong>{salesExecutive}</strong>
            </p>
            <p>
              Requesting Officer: <strong>{requestingOfficer}</strong>
            </p>
            <p>
              Print Date: <strong>{formatDisplayDate(printDate)}</strong>
            </p>
          </div>
        </section>

        <div className='invoice-table-wrap'>
          <table className='invoice-table'>
            <thead>
              <tr>
                {isExerciseBook ? (
                  <>
                    <th className='invoice-table__center'>Carton</th>
                    <th className='invoice-table__center'>Pack</th>
                    <th className='invoice-table__center'>Copies</th>
                    <th className='invoice-table__center'>Total Qty</th>
                  </>
                ) : (
                  <th className='invoice-table__center'>Qty</th>
                )}
                <th className='invoice-table__title'>Book Title</th>
                <th className='invoice-table__number'>Unit Price</th>
                <th className='invoice-table__number'>Discount(%)</th>
                <th className='invoice-table__number'>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {(items || []).map((item, index) => {
                const qty = Number(item.quantity) || 0
                const cartons =
                  item.cartons !== undefined && item.cartons !== null
                    ? item.cartons
                    : Math.floor(qty / 200)
                const packs =
                  item.packs !== undefined && item.packs !== null
                    ? item.packs
                    : Math.floor((qty % 200) / 20)
                const copies =
                  item.copies !== undefined && item.copies !== null
                    ? item.copies
                    : qty % 20

                return (
                  <tr key={`${item.description}-${index}`}>
                    {isExerciseBook ? (
                      <>
                        <td className='invoice-table__center'>{cartons}</td>
                        <td className='invoice-table__center'>{packs}</td>
                        <td className='invoice-table__center'>{copies}</td>
                        <td className='invoice-table__center'>
                          {item.quantity}{' '}
                          {item.totalReturned > 0
                            ? `-(${item.totalReturned})`
                            : ''}
                        </td>
                      </>
                    ) : (
                      <td className='invoice-table__center'>
                        {item.quantity}{' '}
                        {item.totalReturned > 0
                          ? `-(${item.totalReturned})`
                          : ''}
                      </td>
                    )}
                    <td className='invoice-table__title'>{item.description}</td>
                    <td className='invoice-table__number'>
                      {formatNumber(item.unitCost)}
                    </td>
                    <td className='invoice-table__number'>
                      {formatPercentage(item.discount)}
                    </td>
                    <td className='invoice-table__number'>
                      {formatNumber(item.subtotal)}{' '}
                      {item.totalReturned > 0
                        ? `-(${formatNumber(item.totalReturned * item.unitCost - item.totalReturned * item.unitCost * (item.discount / 100))})`
                        : ''}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <section className='invoice-summary'>
          <div className='invoice-summary__box'>
            <div>
              <span>
                {summary.hasReturns ? 'Original subtotal:' : 'Subtotal:'}
              </span>
              <strong>{formatCurrency(summary.original.grossTotal)}</strong>
            </div>
            <div>
              <span>
                {summary.hasReturns ? 'Original discount:' : 'Discount:'}
              </span>
              <strong>{formatCurrency(summary.original.discountTotal)}</strong>
            </div>
            <div className='invoice-summary__total'>
              <span>{summary.hasReturns ? 'Original total:' : 'Total:'}</span>
              <strong>{formatCurrency(summary.original.NetTotal)}</strong>
            </div>
            {summary.hasReturns && (
              <>
                <div className='invoice-summary__after-return'>
                  <span>After return:</span>
                  <strong>
                    {formatCurrency(summary.afterReturn.grossTotal)}
                  </strong>
                </div>
                <div className='invoice-summary__after-return'>
                  <span>Discount after return:</span>
                  <strong>
                    {formatCurrency(summary.afterReturn.discountTotal)}
                  </strong>
                </div>
                <div className='invoice-summary__total invoice-summary__after-return'>
                  <span>Total after return:</span>
                  <strong>
                    {formatCurrency(summary.afterReturn.NetTotal)}
                  </strong>
                </div>
              </>
            )}
          </div>
        </section>

        <div className='invoice-actions' data-html2canvas-ignore='true'>
          {isPdfDownload ? (
            downloadError ? (
              <button
                type='button'
                onClick={() => {
                  downloadStartedRef.current = false
                  setDownloadError('')
                  createElementPdf(invoiceSheetRef.current)
                    .then(setPdfBlob)
                    .catch((downloadRequestError) =>
                      setDownloadError(downloadRequestError.message),
                    )
                }}
              >
                Try again
              </button>
            ) : pdfBlob ? (
              <PdfActions
                pdfBlob={pdfBlob}
                filename={`invoice-${invoiceId}.pdf`}
                onPrint={printInvoice}
                onError={setDownloadError}
              />
            ) : (
              <span>Preparing PDF...</span>
            )
          ) : (
            <button type='button' onClick={printInvoice}>
              <span aria-hidden='true'>🖨</span> Print
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Invoice
